import { motion } from "framer-motion";
import { useMatch } from "@/match/MatchContext";
import { NAV_SKIP_LABEL } from "@/data/content";

export function LogoMark({ className = "w-7 h-7" }) {
  return (
    <svg
      viewBox="0 0 32 32"
      className={className}
      data-testid="brand-logo-mark"
      aria-hidden="true"
    >
      <rect x="0" y="0" width="32" height="32" fill="#84E116" stroke="#FFFFFF" strokeWidth="2" />
      <rect x="15" y="2" width="2" height="28" fill="#0A0A0A" />
      <circle cx="16" cy="16" r="6.5" fill="none" stroke="#0A0A0A" strokeWidth="2" />
      <circle cx="16" cy="16" r="2.6" fill="#0A0A0A" />
    </svg>
  );
}

export default function Scoreboard() {
  const { board, scrollTo } = useMatch();
  const [home, away] = board.score;

  return (
    <motion.header
      data-testid="scoreboard-header"
      className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A] text-white border-b-4 border-black"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ delay: 3.1, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 h-14 sm:h-16 flex items-center justify-between gap-3">
        {/* left — branding */}
        <button
          type="button"
          onClick={() => scrollTo("#kickoff")}
          className="flex items-center gap-2.5 min-w-0 cursor-pointer"
          data-testid="scoreboard-logo"
        >
          <LogoMark className="w-6 h-6 sm:w-7 sm:h-7 shrink-0" />
          <span className="font-broadcast font-extrabold text-[11px] sm:text-sm tracking-[0.22em] uppercase truncate">
            Advaith <span className="text-[#84E116]">vs</span> Life
          </span>
        </button>

        {/* centre — score bug */}
        <div className="flex items-center gap-2 sm:gap-4" data-testid="scoreboard-score">
          <span className="hidden sm:inline font-broadcast text-[10px] tracking-[0.2em] uppercase text-white/60">
            Advaith
          </span>
          <div className="flex items-center gap-1.5 sm:gap-2.5">
            <span className="font-broadcast font-extrabold text-lg sm:text-2xl text-[#84E116] bg-white/5 border border-white/15 px-2 sm:px-3 py-0.5">
              {home}
            </span>
            <span className="font-broadcast font-bold text-[#84E116] text-lg sm:text-2xl">—</span>
            <span className="font-broadcast font-extrabold text-lg sm:text-2xl text-[#84E116] bg-white/5 border border-white/15 px-2 sm:px-3 py-0.5">
              {away}
            </span>
          </div>
          <span className="hidden sm:inline font-broadcast text-[10px] tracking-[0.2em] uppercase text-white/60">
            Life
          </span>
          <span className="hidden md:block w-px h-6 bg-white/20 mx-1" />
          <span
            className="hidden md:flex items-center gap-2 font-broadcast font-extrabold text-base sm:text-xl tracking-widest whitespace-nowrap"
            data-testid="scoreboard-timer"
          >
            {board.mode === "match" && <span className="w-2 h-2 bg-[#FF3B30] live-dot" />}
            {board.mode === "fulltime" && (
              <span className="bg-[#84E116] text-black px-2 py-0.5 text-xs sm:text-sm tracking-[0.2em]">
                FULL TIME
              </span>
            )}
            {board.mode === "penalties" ? board.clock : board.mode === "match" ? board.clock : null}
          </span>
        </div>

        {/* right — skip */}
        <button
          type="button"
          onClick={() => scrollTo("#final")}
          data-testid="skip-to-portfolio-btn"
          className="group font-broadcast font-bold text-[10px] sm:text-xs tracking-[0.18em] uppercase text-white hover:text-[#84E116] transition-colors duration-200 flex items-center gap-1.5 whitespace-nowrap"
        >
          {NAV_SKIP_LABEL.replace(/\s*→\s*$/, "")}
          <span className="inline-block transition-transform duration-200 group-hover:translate-x-1">→</span>
        </button>
      </div>
    </motion.header>
  );
}
